import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Heart, 
  Users, 
  Home, 
  Clock, 
  Pill, 
  Sparkles,
  ArrowRight 
} from 'lucide-react';
import { SmartLink } from './SmartLink';

const services = [
  {
    id: 'personal-care',
    icon: Heart,
    title: 'Personal Care',
    description: 'Dignified assistance with bathing, grooming, dressing, and personal hygiene that respects independence while ensuring comfort and safety.',
    color: 'soft-gold',
    bgColor: 'bg-soft-gold/10',
    size: 'large',
  },
  {
    id: 'companionship',
    icon: Users,
    title: 'Companionship',
    description: 'Meaningful social interaction, conversation, and emotional support that brightens each day and combats loneliness.',
    color: 'deep-blue',
    bgColor: 'bg-deep-blue/10',
    size: 'medium',
  },
  {
    id: 'daily-living',
    icon: Home,
    title: 'Daily Living Support',
    description: 'Help with meal preparation, light housekeeping, laundry, and errands to maintain a comfortable, organized home environment.',
    color: 'muted-green',
    bgColor: 'bg-muted-green/10',
    size: 'medium',
  },
  {
    id: 'respite-care',
    icon: Sparkles,
    title: 'Respite Care',
    description: 'Reliable temporary relief for family caregivers, providing peace of mind while you take time for yourself.',
    color: 'navy',
    bgColor: 'bg-navy/10',
    size: 'medium',
  },
  {
    id: 'medication',
    icon: Pill,
    title: 'Medication Reminders',
    description: 'Gentle reminders to help maintain medication schedules, supporting health and wellness routines.',
    color: 'soft-gold',
    bgColor: 'bg-soft-gold/10',
    size: 'medium',
  },
  {
    id: 'non-medical',
    icon: Clock,
    title: 'Non-Medical Care',
    description: 'Comprehensive support services that help your loved ones thrive at home without clinical interventions.',
    color: 'deep-blue',
    bgColor: 'bg-deep-blue/10',
    size: 'large',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

export const ServicesSection = () => {
  return (
    <section id="services-section" className="relative py-24 md:py-32 bg-white overflow-hidden" data-testid="services-section">
      {/* Background Decorations */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-gradient-to-bl from-soft-gold/5 to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-gradient-to-tr from-deep-blue/5 to-transparent rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 md:mb-20"
        >
          <span className="inline-block text-sm font-semibold uppercase tracking-[0.2em] text-soft-gold mb-4">
            Our Services
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-navy mb-6">
            What We Do at <span className="text-gradient-gold">Heritage Home Health LLC</span>
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            We provide comprehensive, personalized in-home care services designed to support 
            independence, comfort, and quality of life for your loved ones.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {services.map((service, index) => {
            const Icon = service.icon;
            const isLarge = service.size === 'large';
            
            return (
              <motion.div
                key={service.id}
                variants={itemVariants}
                className={`group relative ${isLarge ? 'md:col-span-2 lg:col-span-1' : ''}`}
              >
                <div
                  className={`relative h-full p-8 rounded-3xl transition-all duration-500 ${service.bgColor} border border-transparent hover:border-soft-gold/20 hover:shadow-soft-3d-lg hover:-translate-y-2`}
                  data-testid={`service-card-${service.id}`}
                >
                  {/* Icon */}
                  <div
                    className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 ${
                      service.color === 'soft-gold'
                        ? 'bg-soft-gold/20'
                        : service.color === 'deep-blue'
                        ? 'bg-deep-blue/20'
                        : service.color === 'muted-green'
                        ? 'bg-muted-green/20'
                        : 'bg-navy/20'
                    }`}
                  >
                    <Icon
                      className={`w-7 h-7 ${
                        service.color === 'soft-gold'
                          ? 'text-soft-gold'
                          : service.color === 'deep-blue'
                          ? 'text-deep-blue'
                          : service.color === 'muted-green'
                          ? 'text-muted-green'
                          : 'text-navy'
                      }`}
                    />
                  </div>

                  {/* Content */}
                  <h3 className="font-serif text-2xl text-navy mb-3 group-hover:text-soft-gold transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed mb-6">
                    {service.description}
                  </p>

                  {/* Learn More Link */}
                  <SmartLink
                    to={`/what-we-do#${service.id}`}
                    className="inline-flex items-center text-sm font-semibold text-navy group-hover:text-soft-gold transition-colors"
                    data-testid={`service-${service.id}-learn-more`}
                  >
                    <span>Learn More</span>
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </SmartLink>

                  {/* Decorative Corner */}
                  <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-bl from-white/50 to-transparent rounded-tr-3xl rounded-bl-[80px] opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="text-center mt-16"
        >
          <Link
            to="/what-we-do"
            data-testid="services-view-all-btn"
            className="group inline-flex items-center space-x-2 bg-navy text-white px-8 py-4 rounded-full text-base font-semibold hover:bg-deep-blue transition-all duration-300 shadow-soft-3d hover:shadow-soft-3d-lg hover:-translate-y-1"
          >
            <span>View All Services</span>
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesSection;
